import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  handleFetchPredictors,
  handleDeletePredictor,
  handleUpdatePredictor,
  handleRefreshPredictors,
} from "../../modules/predictorModule.ts";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmModal.tsx";
import ReusableTable from "../../components/common/Table.tsx";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {toast, ToastContainer} from 'react-toastify';import {
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from "material-react-table";
import SyncIcon from '@mui/icons-material/Sync';
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { MoreHoriz, MoreVert } from "@mui/icons-material";
import { RiDeleteBin5Line, RiEyeLine, RiScan2Line, RiScanLine, RiUpload2Line } from "react-icons/ri";
import StatusBadge from "../../components/common/Badge.tsx";

interface Predictor {
  problem_type: string;
  predictor_name: string;
  name?: string;
  domain: string;
  created_at: string;
  modified_at: string;
  training_status: string;
  id: number;
  type: string;
  is_incremental: Boolean;
  preprocess_id: number;
  train_start_time?: string;
  train_end_time?: string;
  data_source: string;
}

const TrainingModal = () => {
  const navigate = useNavigate();
  //const toast = useToast();

  const [predictors, setPredictors] = useState<Predictor[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [anchorEl, setAnchorEl] = useState<{
    [key: number]: HTMLElement | null;
  }>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Predictor | null>(null);
  const [selectedPredictor, setSelectedPredictor] = useState<Predictor | null>(
    null
  );

  const getDomainDisplayName = (domain: string): string => {
    const domainMapping: { [key: string]: string } = {
      "EnergyAnomalyDetection": "Energy Anomaly Detection",
      "CoolingSystemEfficiencyDetection": "Cooling System Efficiency Detection",
      "RANSiteFailurePrediction": "RAN Site Failure Prediction",
      "Churn": "Churn",
      "Forecast": "Forecast",
      "Network_Quality_of_Experience_QoE_Score_Prediction" : "Network QOE Score Prediction",
      "AnomalyDetection" : "Anomaly Detection",
      "UsageForecast": "Usage Forecast",
      "EventVolumeForecast": "Event Volume Forecast",
    };

    return domainMapping[domain] || domain;
  };

  const getProblemDisplayName = (problem_type: string): string => {
    const problemTypeMapping: { [key: string]: string } = {
      "Classification": "Classification",
      "Forecast": "Forecast",
      "Regression": "Regression",
      "UnsupervisedLearning": "Unsupervised Learning"
    };

    return problemTypeMapping[problem_type] || problem_type;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const formatTime = (timeString: string | null | undefined): string => {
    if (!timeString) return "";

    // Parse time string (assuming format like "12:31:40.526714")
    const timeParts = timeString.split(':');
    if (timeParts.length < 2) return timeString;

    const hours = parseInt(timeParts[0]);
    const minutes = timeParts[1];
    const seconds = timeParts[2] ? timeParts[2].split('.')[0] : '00';

    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    return `${displayHours}:${minutes}:${seconds} ${period}`;
  };

  const fetchPredictors = async () => {
    setLoading(true);
    try {
      const skip = pagination.pageIndex * pagination.pageSize;
      const limit = pagination.pageSize;
      const page = pagination.pageIndex + 1;

      const response = await handleFetchPredictors(skip, limit, page);

      let predictorsData: Predictor[] = [];
      let totalCount = 0;

      if (Array.isArray(response)) {
        predictorsData = response;
        totalCount = response.length;
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        "total" in response
      ) {
        predictorsData = response.data;
        totalCount = response.total;
      }

      const formattedData = predictorsData.map((predictor: Predictor) => ({
        ...predictor,
        created_at: formatDate(predictor.created_at),
        modified_at: formatDate(predictor.modified_at),
        train_start_time: predictor.train_start_time,
        train_end_time: predictor.train_end_time,
      }));

      setPredictors(formattedData);
      setTotalRows(totalCount);
    } catch (error) {
      console.error("Failed to fetch predictors:", error);
      setError("Failed to fetch predictors.");
    } finally {
      setLoading(false);
    }
  };

  const refreshPredictors = async () => {
    setLoading(true);
    try {
      const skip = pagination.pageIndex * pagination.pageSize;
      const limit = pagination.pageSize;
      const page = pagination.pageIndex + 1;

      const response = await handleRefreshPredictors(skip, limit, page);

      let predictorsData: Predictor[] = [];
      let totalCount = 0;

      if (Array.isArray(response)) {
        predictorsData = response;
        totalCount = response.length;
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        "total" in response
      ) {
        predictorsData = response.data;
        totalCount = response.total;
      }

      const formattedData = predictorsData.map((predictor: Predictor) => ({
        ...predictor,
        created_at: formatDate(predictor.created_at),
        modified_at: formatDate(predictor.modified_at),
        train_start_time: predictor.train_start_time,
        train_end_time: predictor.train_end_time,
      }));

      setPredictors(formattedData);
      setTotalRows(totalCount);
    } catch (error) {
      console.error("Failed to fetch predictors:", error);
      setError("Failed to fetch predictors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPredictors();
  }, []);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: number
  ) => {
    setAnchorEl((prev) => ({ ...prev, [id]: event.currentTarget }));
  };

  const handleMenuClose = (id: number) => {
    setAnchorEl((prev) => ({ ...prev, [id]: null }));
  };

  const handlePredictionMethodContinue = (method: "batch" | "form", Predictor: Predictor) => {
    if (!Predictor) return;

    // Close the menu
    handleMenuClose(Predictor.id);

    if (Predictor.problem_type === "Forecast") {
      navigate(`/forecast/${Predictor.id}`, {
        state: { predictor: Predictor },
      });
      return; // Add this return to prevent further execution
    }

    setSelectedPredictor(Predictor);

    if (method === "batch") {
      navigate(`/batch-prediction/${Predictor?.id}`, {
        state: { predictor: Predictor }
      });
    } else {
      navigate(`/form-prediction/${Predictor?.id}`, {
        state: {
          name: Predictor.predictor_name,
          problem_type: Predictor.problem_type
        }
      });
    }
  };

  const handleUpdatePredictorConfirm = async (train_id: number, preprocessId: number) => {
    try {
      // Close the menu first
      handleMenuClose(train_id);

      await handleUpdatePredictor(train_id, preprocessId);

      const skip = pagination.pageIndex * pagination.pageSize;
      const limit = pagination.pageSize;
      const page = pagination.pageIndex + 1;

      const response = await handleFetchPredictors(skip, limit, page);

      let predictorsData: Predictor[] = [];
      let totalCount = 0;

      if (Array.isArray(response)) {
        predictorsData = response;
        totalCount = response.length;
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        "total" in response
      ) {
        predictorsData = response.data;
        totalCount = response.total;
      }

      const formattedData = predictorsData.map((predictor: Predictor) => ({
        ...predictor,
        created_at: formatDate(predictor.created_at),
        modified_at: formatDate(predictor.modified_at),
        train_start_time: predictor.train_start_time,
        train_end_time: predictor.train_end_time,
      }));

      setPredictors(formattedData);
      setTotalRows(totalCount);

      toast.success(response?.data?.data || "Incremental Learning Successfully Initiated");
    } catch (error: any) {
      toast.error(
        ` ${error?.response?.data?.detail || "Please try again."}`
      );
    }
  };

  const getStatusClasses = (status) => {
    switch (status) {
      case "Pending":
      case "In Progress":
        return "bg-light-100 text-black-800 border border-yellow-400";
      case "Completed":
      case "true":
        return "bg-light-100 text-black-800 border border-green-400";
      case "Failed":
      case "false":
        return "bg-light-100 text-black-800 border border-red-400";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-300";
    }
  };

  const handleDeleteClick = (id: number, type: string, name?: string) => {
    setItemToDelete({
      id,
      type,
      name,
      predictor_name: "",
      problem_type: "",
      created_at: "",
      modified_at: "",
      training_status: "",
      domain: "",
      is_incremental: false,
    });
    setIsDeleteModalOpen(true);
    handleMenuClose(id);
  };

  const refreshTable = () => {
    refreshPredictors();
  }

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleteLoading(true);
    try {
      await handleDeletePredictor(itemToDelete.id);

      const skip = pagination.pageIndex * pagination.pageSize;
      const limit = pagination.pageSize;
      const page = pagination.pageIndex + 1;

      const response = await handleFetchPredictors(skip, limit, page);

      let predictorsData: Predictor[] = [];
      let totalCount = 0;

      if (Array.isArray(response)) {
        predictorsData = response;
        totalCount = response.length;
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        "total" in response
      ) {
        predictorsData = response.data;
        totalCount = response.total;
      }

      const formattedData = predictorsData.map((predictor: Predictor) => ({
        ...predictor,
        created_at: formatDate(predictor.created_at),
        modified_at: formatDate(predictor.modified_at),
        train_start_time: predictor.train_start_time,
        train_end_time: predictor.train_end_time,
      }));

      setPredictors(formattedData);
      setTotalRows(totalCount);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      toast.success("Predictor deleted successfully");
    } catch (error: any) {
      setIsDeleteModalOpen(false);
      toast.error(
        `Failed to delete ${itemToDelete.type}. ${error.message || "Please try again."
        }`
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = useMemo<MRT_ColumnDef<Predictor>[]>(
    () => [
      {
        accessorKey: "actions",
        header: " ",
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        size: 10,
        Cell: ({ row }) => (
          <>
            <IconButton onClick={(e) => handleMenuClick(e, row.original.id)}>
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl[row.original.id]}
              open={Boolean(anchorEl[row.original.id])}
              onClose={() => handleMenuClose(row.original.id)}
              sx={{
                "& .MuiPaper-root": {
                  backgroundColor: "#fff",
                  width: "180px",
                },
              }}
            >
              {row.original.problem_type !== "Forecast" && (
                <MenuItem
                  onClick={() => handlePredictionMethodContinue("batch", row.original)}
                  sx={{
                    color: "black",
                    paddingBottom: "15px",
                    "&:hover": { backgroundColor: "#f4f4f4ff" },
                  }}
                  disabled={row.original.training_status !== "Completed" || row.original.problem_type === "UnsupervisedLearning"}
                >
                  <RiScan2Line size={18} />&nbsp;&nbsp;Batch Prediction
                </MenuItem>
              )}
              <MenuItem
                onClick={() => handlePredictionMethodContinue("form", row.original)}
                sx={{
                  color: "black",
                  paddingBottom: "15px",
                  "&:hover": { backgroundColor: "#f4f4f4ff" },
                }}
                disabled={row.original.training_status !== "Completed" || row.original.data_source === "datalake"}
              >
                <RiScanLine size={18} />&nbsp;&nbsp;{row.original.problem_type === "Forecast" ? "Forecast" : "Form Prediction"}
              </MenuItem>
              <MenuItem
                onClick={() => navigate(`/modal-details/${row.original.id}`, {
                  state: {
                    datasource: row.original.data_source,
                    name: row.original.predictor_name,
                    predictor_details: row.original,
                  }
                })}
                disabled={row.original.training_status !== "Completed"}
                sx={{
                  color: "black",
                  paddingBottom: "15px",
                  "&:hover": { backgroundColor: "#f4f4f4ff" },
                }}
              >
                <RiEyeLine size={18} />&nbsp;&nbsp;View
              </MenuItem>
              {row.original.is_incremental ?
                <MenuItem
                  onClick={() =>
                    handleUpdatePredictorConfirm(
                      row.original.id,
                      row.original.preprocess_id
                    )
                  }
                  disabled={row.original.training_status !== "Completed"}
                  sx={{
                    color: "black",
                    paddingBottom: "15px",
                    "&:hover": { backgroundColor: "#f4f4f4ff" },
                  }}
                >
                  <RiUpload2Line size={18} />&nbsp;&nbsp;Update Model
                </MenuItem> : null}
              <MenuItem
                onClick={() => handleDeleteClick(row.original.id, "prediction")}
                disabled={row.original.training_status !== "Completed"}
                sx={{
                  color: "black",
                  paddingBottom: "15px",
                  "&:hover": { backgroundColor: "#f4f4f4ff" },
                }}
              >
                <RiDeleteBin5Line size={18} />&nbsp;&nbsp;Delete
              </MenuItem>
            </Menu>
          </>
        ),
      },
      {
        accessorKey: "predictor_name",
        header: "Predictor Name",
      },
      {
        accessorKey: "problem_type",
        header: "Predictor Type",
        Cell: ({ row }) => {
          return getProblemDisplayName(row.original.problem_type);
        }
      },
      {
        accessorKey: "training_status",
        header: "Training Status",
        Cell: ({ row, cell }) => {
          let statusValue = cell.getValue().toString();
          return statusValue ? <StatusBadge value={statusValue} isCustomerTag={false} /> : "-";
        }
      },
      {
        accessorKey: "domain",
        header: "Domain",
        Cell: ({ row }) => {
          return getDomainDisplayName(row.original.domain);
        }
      },
      {
        accessorKey: "dataset_name",
        header: "Dataset Name"
      },
      {
        accessorKey: "created_at",
        header: "Date Created",
      },
      {
        accessorKey: "modified_at",
        header: "Date Modified",
      },
      {
        accessorKey: "train_start_time",
        header: "Start Time",
        Cell: ({ row }) => {
          return row.original.train_start_time ? formatTime(row.original.train_start_time) : '-';
        }
      },
      {
        accessorKey: "train_end_time",
        header: "End Time",
        Cell: ({ row }) => {
          return row.original.train_end_time ? formatTime(row.original.train_end_time) : '-';
        }
      },
      {
        accessorKey: "is_incremental",
        header: "Incremental Learning",
        Cell: ({ row, cell }) => {
          let statusValue = cell.getValue().toString() === "false" ? "Disabled" : "Enabled";

          return statusValue ? <StatusBadge value={statusValue} isCustomerTag={false} /> : "-";
        },
      }
    ],
    [anchorEl]
  );



  return (
    <>
      <BreadCrumb moduleName={"Training"} pageName={"Predictor List"} />
      <ToastContainer position="top-center" />
      <div className="d-flex flex-sm-row flex-column w-100 justify-content-between align-items-lg-center gap-2 mb-1 mt-0">
        <div className="d-flex flex-row gap-2">
          <h5 className="rv-page-title p-2">Predictors List</h5>
        </div>
        <div className="d-flex align-items-center">
          <button
            type="button"
            onClick={() => navigate("/CreatePredictor")}
            className="btn-label rv-primary-btn me-2 rounded-1 px-4 py-1 text-nowrap btn btn-secondary"
          >
            <i className="ri-add-line label-icon align-middle fs-16 me-1"></i>
            <span className="ps-3 ms-2 me-1">Add Predictor</span>
          </button>
          {predictors?.find((predictor) => predictor?.training_status !== "Completed") &&
            (<SyncIcon
              className="ms-2"
              onClick={refreshTable} />)
          }
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemType={itemToDelete?.type || ""}
        itemName={itemToDelete?.name}
        loading={deleteLoading}
      />

      <div className="container-fluid px-0">
        <div className="disable-margin-card table-responsive table-customer border table-border w-100">
          <ReusableTable
            columns={columns}
            data={predictors}
            loading={loading}
            error={error}
            pagination={pagination}
            onPaginationChange={setPagination}
          />
        </div>
      </div>
    </>
    // <>
    // <BreadCrumb moduleName={"Training"} pageName={"Predictor List"}/>
    // <div className="p-4 md:p-2">
    //   <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
    //     <h1 className="text-base font-bold">PREDICTOR LIST</h1>
    //     <div className="flex items-center gap-2">
    //       <button
    //         onClick={() => navigate("/CreatePredictor")}
    //         className="w-full sm:w-auto px-8 py-2 bg-[#2F99A8] text-white rounded-lg hover:bg-[#268696]"
    //       >
    //         Create Predictor
    //       </button>
    //       {predictors?.find((predictor) => predictor?.training_status !== "Completed") &&
    //         (<SyncIcon
    //           className="ms-2"
    //           onClick={refreshTable} />)
    //       }
    //     </div>
    //   </div>

    //   <ReusableTable
    //     columns={columns}
    //     data={predictors}
    //     loading={loading}
    //     error={error}
    //     pagination={pagination}
    //     onPaginationChange={setPagination}
    //   />

    //   <DeleteConfirmationModal
    //     isOpen={isDeleteModalOpen}
    //     onClose={() => setIsDeleteModalOpen(false)}
    //     onConfirm={handleDeleteConfirm}
    //     itemType={itemToDelete?.type || ""}
    //     itemName={itemToDelete?.name}
    //     loading={deleteLoading}
    //   />
    // </div>
    // </>
  );
};

export default TrainingModal;